import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  map,
  Subscription,
  switchMap,
  take,
  tap,
  filter,
} from 'rxjs';

import { AuthUser } from '@shared/signup/models';
import { RecurlyBillingInfo, RecurlySubscription } from '@shared/recurly/models';
import { PaymentPlanCodes } from '@shared/price-plan/enums';
import { PricePlan } from '@shared/price-plan/models';
import { SubscriptionTypes } from '@shared/restaurant/enums';
import { SubscriptionState } from '@shared/recurly/enums';
import { makeMap } from '@shared/core';
import {
  DropdownFilterItem,
  OrderByValues,
} from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import {
  AuthAccountService,
  AuthLocationService,
  AuthRestaurantService,
  PricePlansApiService,
} from '@tablesready/frontend-store';
import { LocationSwitcherService } from '@tablesready/frontend-location-switcher';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { RecurlyService } from '../../services';
import { LocationExtended } from '../models';
import { UpdateBillingComponent } from '../modals';

const CENTS = 100;
const MAX_PAGE_SIZE = 5;

@Component({
  selector: 'tablesready-account-billing',
  templateUrl: './account-billing.component.html',
  styleUrls: ['./account-billing.component.scss'],
})
export class AccountBillingComponent implements OnInit, OnDestroy {
  dropdownPricePlans: Array<DropdownFilterItem<string>> = [];
  pricePlansMap: Record<string, PricePlan> = {};
  accountSubscriptionType?: SubscriptionTypes;
  locationsFiltered: LocationExtended[] = [];
  subscriptionTypes = SubscriptionTypes;
  isNotUsaOrCanadaRestaurant = false;
  locations: LocationExtended[] = [];
  pricePlans: PricePlan[] = [];
  billingInfo?: RecurlyBillingInfo;
  paymentInfoVisible = true;
  iconColor = AppIconColor;
  accountCode?: string;
  recurlyKey?: string;
  timezone?: string;
  email?: string;
  licenceExpirationDate?: number;
  limit?: number;
  discountCode?: string;
  licences: number = 0;
  icon = AppIcon;
  sortAsc: boolean;
  sortBy = '';
  countOfFreeLocations: number;
  pricePlanFilter?: string;

  locationPageIndex = 0;
  locationPageSize = MAX_PAGE_SIZE;
  maxDisplayPages = MAX_PAGE_SIZE;

  subscriptions?: RecurlySubscription[] = [];
  private user?: AuthUser;
  private readonly filtersSource$ = new BehaviorSubject<Record<string, unknown>>({ });
  private readonly subscription = new Subscription();

  // eslint-disable-next-line max-params
  constructor(
    private readonly locationSwitcherService: LocationSwitcherService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly pricePlansApiService: PricePlansApiService,
    private readonly locationService: AuthLocationService,
    private readonly recurlyService: RecurlyService,
    private readonly accountService: AuthAccountService,
    private readonly modalController: FrontendModalService,
    private readonly router: Router,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) { }

  ngOnInit(): void {
    const keySub = this.recurlyService.getApiKey()
      .subscribe((key) => {
        this.recurlyKey = String(key);
      });

    const filterSub = this.filtersSource$.pipe()
      .subscribe((filters) => {
        this.locationsFiltered = this.locations.filter((loc) => {
          let isMatch = true;

          Object.entries(filters).forEach(([key, value]) => {
            const containsString = String(loc[key]).toLowerCase()
              .includes(String(value).toLowerCase());

            if (!containsString && value !== undefined && value !== null && value !== '') {
              isMatch = false;
            }
          });

          return isMatch;
        });
      });

    const authSub = combineLatest([
      this.authRestaurantService.select(),
      this.pricePlansApiService.getByAccount().pipe(tap(pricePlans => {
        this.pricePlans = pricePlans.map(item => ({
          ...item,
          limit: this.isNotUsaOrCanadaRestaurant ? Math.ceil(item.limit * 0.1) : item.limit,
        }));
        this.pricePlansMap = makeMap(pricePlans, 'plan_code');
      })),
    ]).pipe(
      filter(([user]) => user.account !== undefined),
      tap(([user]) => {
        this.user = user;
        this.discountCode = user.account.recurly.coupon;
        this.email = user.account.recurly.email;
        this.billingInfo = user.account.recurly.billing_info;
        this.timezone = user.location.timezone;
        this.accountCode = user.account.recurly.account_code;
        this.accountSubscriptionType = user.account.subscriptionType;
        this.isNotUsaOrCanadaRestaurant = !['US', 'CA'].includes(user.location.country);
        this.subscriptions = user.account.recurly.subscriptions || [];
        this.licenceExpirationDate = user.account.expirationDate;
        this.limit = user.account.limit;
        this.licences = user.account.licenses;
      }),
      map(([user]) => user.account.id),
      switchMap(accountId => this.locationService.selectByAccountId(accountId)),
      tap(locations => {
        this.countOfFreeLocations = locations.filter(location => location?.recurly?.planCode === PaymentPlanCodes.Free && location.enabled).length;
        this.locations = locations.map(loc => {
          const currentSub = this.subscriptions.find(sub => sub.uuid === loc.recurly.subscriptionUuid);
          if (!currentSub) {
            return {
              ...loc,
              periodEnds: 0,
              pricePlanPrice: '-',
              planCode: '',
              subscriptionState: SubscriptionState.notFound,
            };
          }

          const pricePlan = this.pricePlans.find(plan => plan.plan_code === currentSub.plan_code);

          return {
            ...loc,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            subscriptionState: loc.recurly.subscription_state || currentSub.state as SubscriptionState,
            periodEnds: currentSub.expiration_date,
            planCode: currentSub?.plan_code,
            pricePlanPrice: pricePlan ? String(pricePlan.price / CENTS) : '-',
          };
        });

        const presentPlanCodes = this.locations.map(loc => loc.planCode);
        this.dropdownPricePlans = this.pricePlans.map(({ plan_code, name }) => ({
          value: plan_code,
          title: name,
        })).filter(plan => presentPlanCodes.includes(plan.value));
        this.filtersSource$.next({ });
      }),
    ).subscribe();

    this.subscription.add(keySub);
    this.subscription.add(authSub);
    this.subscription.add(filterSub);
  }

  orderBy({ orderBy, orderAscending }: OrderByValues): void {
    this.sortAsc = orderAscending;
    this.sortBy = orderBy;

    this.locationsFiltered = this.locationsFiltered.sort((a, b) => {
      let result = a.name.localeCompare(b.name);

      if (orderBy === 'period') {
        result = Number(a.periodEnds) - Number(b.periodEnds);
      }

      return result;
    });

    if (orderAscending) {
      this.locationsFiltered = this.locationsFiltered.reverse();
    }
  }

  manageLocation(locationId: string): void {
    this.locationSwitcherService.selectLocation(locationId);
    void this.router.navigateByUrl('/settings/location/billing');
  }

  setFilters(filter: Partial<Record<string, unknown>>): void {
    this.filtersSource$.next({
      ...this.filtersSource$.getValue(),
      ...filter,
    });
  }

  updateBillingModal(firstTimeAdding = false): void {
    this.modalController.create<UpdateBillingComponent, unknown>(UpdateBillingComponent, {
      billingInfo: this.billingInfo,
      billingEmail: this.email,
      recurlyApiKey: this.recurlyKey,
      firstTimeAdding,
      user: this.user,
      title: firstTimeAdding ? 'Adding Payment Info' : 'Updating Payment Info',
    })
      .pipe(
        take(1),
        concatMap(() => this.accountService.load()),
        take(1),
      )
      .subscribe();
  }

  get showBillingInfo(): boolean {
    if (this.isOffline) {
      return !!this.billingInfo?.card_type;
    }

    return true;
  }

  get isOffline(): boolean {
    return this.accountSubscriptionType === SubscriptionTypes.offline;
  }

  get start(): number {
    return (this.locationPageIndex - 1) * this.locationPageSize;
  }

  get end(): number {
    return (this.locationPageIndex - 1) * this.locationPageSize + this.locationPageSize
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
